<template>
  <div class="filter-accommodation-types-container">
    <div class="accommodation-types">
      <div
        v-for="accommodationType in mainAccommodationTypes ?? []"
        :key="accommodationType.uuid"
        class="accommodation-type"
      >
        <ElButton
          :type="accommodationType.selected ? 'primary active' : 'primary'"
          @click="selectedAccommodationType(accommodationType)"
        >
          <Icons
            :name="
              accommodationType.selected
                ? `${accommodationType.icon}_white`
                : accommodationType.icon
            "
          />
          <span>
            {{ accommodationType.type }}
          </span>
        </ElButton>
      </div>
    </div>
    <div class="view-all" v-if="otherAccommodationTypes?.length">
      <ElButton type="link dark" @click="$emit('filterDrawer')">
        {{ $t('view_all') }}
      </ElButton>
    </div>
    <div class="actions">
      <ElButton type="link dark" @click="clear()">
        {{ $t('clear') }}
      </ElButton>
      <ElButton type="primary dark" @click="$emit('filter')">
        {{ $t('apply') }}
      </ElButton>
    </div>
  </div>
</template>

<script>
import { ElLoading, ElMessageBox } from 'element-plus'
import { useI18n } from 'vue-i18n'
import { cloneDeep } from 'lodash'

export default defineComponent({
  props: {
    filterAccommodations: {
      type: Object,
      default: () => {}
    }
  },
  setup() {
    const { width, height } = useBreakpoints()
    const { t } = useI18n()
    const { $sentry } = useNuxtApp()
    const router = useRouter()
    const route = useRoute()
    const config = useRuntimeConfig()

    const initialVariables = []

    let objectToReturn = {
      width,
      height,
      t,
      router,
      route,
      config,
      $sentry
    }
    for (const variable of initialVariables) {
      objectToReturn = Object.assign(objectToReturn, variable)
    }

    return objectToReturn
  },
  data() {
    return {
      accommodationTypes: null,
      loading: false,
      loadingDialog: null,
      filterAccommodationTypes: {
        filterField: {
          hasAccommodation: {
            column: 'HASACCOMMODATION',
            value: true
          }
        }
      },
      mainTypes: [
        { type: 'Hotel', icon: '16px/building_light_1' },
        { type: 'Villa', icon: '16px/house_line_light_1' },
        { type: 'Lodge', icon: '16px/lodge' },
        { type: 'Glamping', icon: '16px/tipi_light_1' },
        { type: 'Resort', icon: '16px/building_apartment_light_1' }
      ],
      mainAccommodationTypes: [],
      otherAccommodationTypes: []
    }
  },
  watch: {
    filterAccommodations: {
      deep: true,
      handler(newValue) {
        this.processResponse()
      }
    }
  },
  mounted() {
    this.getAccommodationTypes()
  },
  methods: {
    async getAccommodationTypes(refetch = false) {
      try {
        this.loadingDialog = ElLoading.service({
          fullscreen: true,
          background: '#F7F7F7',
          svg: useLoaderSvg(),
          svgViewBox: useLoaderSvgViewBox()
        })
        const accommodationTypes = await apolloAccommodationTypes(
          {
            sortBy: 'TYPE_EN',
            sortDesc: false,
            search: null,
            page: 1,
            first: 500
          },
          true,
          this.filterAccommodationTypes,
          refetch ? 'network-only' : null
        )

        this.accommodationTypes =
          accommodationTypes?.getAllAccommodationTypes?.data?.map((i) => {
            return {
              ...i,
              selected: false
            }
          })

        this.processResponse()
      } catch (err) {
        this.$showError(err, this.t)
        this.$sentry(err, 'getAccommodationTypes')
      } finally {
        this.loadingDialog.close()
      }
    },
    processResponse() {
      if (
        this.filterAccommodations?.filterField?.accommodationTypes?.value
          ?.length
      ) {
        this.accommodationTypes =
          this.accommodationTypes?.map((i) => {
            if (
              this.filterAccommodations?.filterField?.accommodationTypes?.value?.find(
                (j) => j === i.uuid
              )
            ) {
              return {
                ...i,
                selected: true
              }
            } else {
              return {
                ...i,
                selected: false
              }
            }
          }) ?? []
      }

      this.setMainAccommodationTypes()
    },
    setMainAccommodationTypes() {
      this.mainAccommodationTypes = []
      for (let type of this.mainTypes) {
        const mainType = this.accommodationTypes?.find(
          (i) => i.type_en === type.type
        )
        if (mainType) {
          this.mainAccommodationTypes.push({
            ...mainType,
            icon: type.icon
          })
        }
      }
      this.setOtherAccommodationTypes()
    },
    setOtherAccommodationTypes() {
      this.otherAccommodationTypes = []
      this.accommodationTypes?.forEach((i) => {
        if (!this.mainAccommodationTypes.find((j) => j.uuid === i.uuid)) {
          this.otherAccommodationTypes.push(i)
        }
      })
    },
    selectedAccommodationType(accommodationType) {
      this.accommodationTypes = this.accommodationTypes.map((i) => {
        if (accommodationType.uuid === i.uuid) {
          return {
            ...i,
            selected: i.selected === false ? true : false
          }
        }
        return i
      })
      this.setMainAccommodationTypes()
      this.$emit(
        'selectedAccommodationType',
        this.accommodationTypes.filter((i) => i.selected).map((i) => i.uuid) ??
          []
      )
      this.$emit(
        'selectedAccommodationTypeLabels',
        this.accommodationTypes
          .filter(
            (i) =>
              i.selected && this.mainTypes.find((j) => j.type === i.type_en)
          )
          .map((i) =>
            getAuthUser()?.value?.language?.toLowerCase() === 'es'
              ? i.type_es
              : getAuthUser()?.value?.language?.toLowerCase() === 'fr'
              ? i.type_fr
              : getAuthUser()?.value?.language?.toLowerCase() === 'de'
              ? i.type_de
              : getAuthUser()?.value?.language?.toLowerCase() === 'sv'
              ? i.type_sv
              : getAuthUser()?.value?.language?.toLowerCase() === 'ar'
              ? i.type_ar
              : i.type_en
          ) ?? []
      )
    },
    clear() {
      this.accommodationTypes = this.accommodationTypes.map((i) => {
        return {
          ...i,
          selected: false
        }
      })
      this.setMainAccommodationTypes()
      this.$emit('selectedAccommodationType', [])
      this.$emit('selectedAccommodationTypeLabels', [])
      this.$emit('filter')
    }
  }
})
</script>

<style scoped lang="scss">
.filter-accommodation-types-container {
  max-height: 300px;
  padding-bottom: 10px;
  overflow-y: auto;
  .accommodation-types {
    display: flex;
    flex-wrap: wrap;
    .accommodation-type {
      margin: 0 8px 8px 0;
    }
  }
  .view-all {
    margin-top: 16px;
  }
  .actions {
    margin-top: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}
</style>